import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ClipboardList, Loader2, Mail, RefreshCw, Search } from "lucide-react";
import {
  adminResendChecklist,
  listChecklistDeliveries,
} from "@/lib/api/cv-checklist.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/cv-checklists")({
  head: () => ({
    meta: [
      { title: "Onboarding checklist log · NDLCS Admin" },
      {
        name: "description",
        content:
          "Every CV & ATS onboarding checklist PDF attached and sent to a client, with version, timestamp and email status.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ChecklistLogPage,
});

const TRIGGER_LABEL: Record<string, string> = {
  account_created: "Account created",
  client_resend: "Client re-send",
  admin_resend: "Admin re-send",
};

function ChecklistLogPage() {
  const [search, setSearch] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const qc = useQueryClient();

  const listFn = useServerFn(listChecklistDeliveries);
  const resendFn = useServerFn(adminResendChecklist);

  const q = useQuery({
    queryKey: ["admin-checklist-log", search],
    queryFn: () => listFn({ data: { search: search || undefined, limit: 300 } }),
  });

  const resend = useMutation({
    mutationFn: async (email: string) => resendFn({ data: { email } }),
    onSuccess: (r: any) => {
      toast.success(`Checklist v${r.version} sent.`);
      setResendEmail("");
      qc.invalidateQueries({ queryKey: ["admin-checklist-log"] });
    },
    onError: (e: any) => toast.error(e?.message || "Send failed."),
  });

  const rows = (q.data?.rows ?? []) as any[];

  return (
    <div className="space-y-6 py-6">
      <header>
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-gold">
          <ClipboardList className="h-3.5 w-3.5" /> Career onboarding
        </p>
        <h1 className="font-display text-2xl text-navy">Onboarding checklist log</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every checklist PDF attached and sent to a client — version, trigger, timestamp and
          email status. Current version: {q.data?.currentVersion ?? "—"}.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-4">
        <label className="flex-1 min-w-[200px] text-sm">
          <span className="mb-1 block font-semibold text-navy">Search by email</span>
          <div className="flex items-center gap-2 rounded-md border border-border px-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="client@example.com"
              className="w-full bg-transparent py-2 text-sm outline-none"
            />
          </div>
        </label>
        <label className="flex-1 min-w-[220px] text-sm">
          <span className="mb-1 block font-semibold text-navy">Re-send checklist to</span>
          <input
            value={resendEmail}
            onChange={(e) => setResendEmail(e.target.value)}
            placeholder="client@example.com"
            className="w-full rounded-md border border-border px-3 py-2 text-sm"
          />
        </label>
        <button
          type="button"
          onClick={() => resendEmail && resend.mutate(resendEmail.trim())}
          disabled={!resendEmail || resend.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-navy-foreground disabled:opacity-60"
        >
          {resend.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
          Send
        </button>
        <button
          type="button"
          onClick={() => q.refetch()}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {q.isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading log…
        </p>
      )}
      {q.error && (
        <p className="text-sm text-destructive">{(q.error as any)?.message ?? "Failed to load."}</p>
      )}

      {!q.isLoading && rows.length === 0 && (
        <p className="text-sm text-muted-foreground">No checklists have been sent yet.</p>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Sent</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Version</th>
                <th className="px-3 py-2">Trigger</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">File</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border align-top">
                  <td className="whitespace-nowrap px-3 py-2">
                    {new Date(r.created_at).toLocaleString("en-ZA")}
                  </td>
                  <td className="px-3 py-2">
                    <span className="block font-medium text-navy">{r.client_name || "—"}</span>
                    <span className="block text-xs text-muted-foreground">{r.email}</span>
                  </td>
                  <td className="px-3 py-2">v{r.version}</td>
                  <td className="px-3 py-2">{TRIGGER_LABEL[r.trigger] ?? r.trigger}</td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        r.status === "sent"
                          ? "rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800"
                          : "rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800"
                      }
                    >
                      {r.status}
                    </span>
                    {r.provider && (
                      <span className="ml-2 text-xs text-muted-foreground">{r.provider}</span>
                    )}
                    {r.error_message && (
                      <span className="mt-1 block text-xs text-destructive">{r.error_message}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{r.filename || "—"}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => resend.mutate(r.email)}
                      disabled={resend.isPending}
                      className="rounded-md border border-border px-2 py-1 text-xs font-semibold text-navy disabled:opacity-60"
                    >
                      Re-send
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
